'use client';

import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { HELP_URL } from '@diritto/common/constants/url';
import { CategoryType, CategoryTypeId } from '@diritto/common/content/constants';
import { ContestId } from '@diritto/common/content/contest.constants';
import { numberComma } from '@diritto/common/utils/string';
import { useViewPort } from '@/ui-hooks/useViewPort';
import { PCOffset } from '@/constants/layout';
import { usePromotionEligibilityCheck } from 'app-components/promotions/UsePromotionEligibilityCheck';
import { useAuthState } from 'contexts/AuthProvider';
import { useGetContestVoteCountQuery } from 'queries/contests/useGetContestVoteCountQuery';
import { useGetContestVotePeriodQuery } from 'queries/contests/useGetContestVotePeriodQuery';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { useResponsiveImageMap } from 'ui-hooks/useResponsiveImageMap';
import { useRequestSign } from '@/ui-hooks/useRequestSign';
import { configure } from 'utils/configure';
import { VoteStampSheet } from './VoteStampSheet';

const BASE_URL = `${configure.staticPrefix}/images/contests/4/vote`;

const HELP_QNA_URL = `${HELP_URL}/hc/ko/articles/14598106894991`;

const GENRE_ORDER = [
  CategoryTypeId[CategoryType.Fantasy],
  CategoryTypeId[CategoryType.BL],
  CategoryTypeId[CategoryType.RomanceFantasy],
  CategoryTypeId[CategoryType.Romance],
];

export const VoteTab = () => {
  useInAppHeader({ screen: WebViewScreen.PickAndChoo, title: '2026 제 4회 픽앤츄' });
  useInAppStatusBar(WebViewScreen.PickAndChoo);
  const { width: screenWidth } = useViewPort();
  const contentWidth = useMemo(
    () => (screenWidth > PCOffset ? Math.min(screenWidth, 730) : screenWidth),
    [screenWidth],
  );

  const scale = useMemo(() => contentWidth / 390, [contentWidth]);

  const { isLoggedIn } = useAuthState();
  const requestSign = useRequestSign();

  const contestId = ContestId.Contest2026;
  const { data: voteData } = useGetContestVoteCountQuery(contestId);
  const { data: votePeriod } = useGetContestVotePeriodQuery(contestId);
  const { handleRidiSSoLink, handleUserCertification, isUserCertificated, ridiSsoLinked } =
    usePromotionEligibilityCheck();
  const onClickUserCertification = useCallback(() => {
    if (!isLoggedIn) {
      requestSign();
      return;
    }
    if (!isUserCertificated) {
      handleUserCertification();
    }
  }, [isLoggedIn, requestSign, handleUserCertification, isUserCertificated]);

  const onClickRidiSSoLink = useCallback(() => {
    if (!isLoggedIn) {
      requestSign();
      return;
    }
    if (!ridiSsoLinked) {
      handleRidiSSoLink();
    }
  }, [isLoggedIn, requestSign, handleRidiSSoLink, ridiSsoLinked]);

  const voteDates = useMemo(() => {
    if (!votePeriod) {
      return [];
    }
    const startAt = DateTime.fromJSDate(new Date(votePeriod.startAt)).setZone('Asia/Seoul');
    const endAt = DateTime.fromJSDate(new Date(votePeriod.endAt)).setZone('Asia/Seoul');

    const days = [];
    let current = startAt;
    while (current.startOf('day') <= endAt.startOf('day')) {
      days.push(current.toFormat('yyyy-MM-dd'));
      current = current.plus({ day: 1 });
    }
    return days;
  }, [votePeriod]);

  const today = useMemo(() => {
    const now = DateTime.now().setZone('Asia/Seoul');
    return now.toFormat('yyyy-MM-dd');
  }, []);

  const genreCounts = useMemo(() => {
    const counts = {
      [CategoryTypeId[CategoryType.Fantasy]]: 0,
      [CategoryTypeId[CategoryType.BL]]: 0,
      [CategoryTypeId[CategoryType.RomanceFantasy]]: 0,
      [CategoryTypeId[CategoryType.Romance]]: 0,
    };

    if (voteData?.count) {
      const dailyData = voteData.count[today];
      if (dailyData?.categories) {
        Object.entries(dailyData.categories).forEach(([categoryId, count]) => {
          if (counts[Number(categoryId)] !== undefined) {
            counts[Number(categoryId)] += count;
          }
        });
      }
    }
    return counts;
  }, [voteData, today]);

  const { participationDays, earnedPoints } = useMemo(() => {
    let days = 0;
    if (voteData?.count) {
      Object.values(voteData.count).forEach(dailyData => {
        if ((dailyData.votedContentIds?.length ?? 0) >= 5) {
          days += 1;
        }
      });
    }

    let points = 0;
    if (days >= 7) {
      points = 1000;
    } else if (days >= 5) {
      points = 700;
    } else if (days >= 3) {
      points = 500;
    } else if (days >= 1) {
      points = 300;
    }

    return { participationDays: days, earnedPoints: points };
  }, [voteData]);

  useResponsiveImageMap('image3', 'image-map3');
  useResponsiveImageMap('image5', 'image-map5');
  useResponsiveImageMap('image8', 'image-map8');

  return (
    <div id="vote-page-container" className="flex flex-col items-center w-full bg-white overflow-hidden">
      <div className="relative w-full flex justify-center min-h-[100vh] overflow-hidden">
        <img id="image3" useMap="#image-map3" alt="vote_3" src={`${BASE_URL}/3.webp`} className="w-full h-auto block" />
        {isUserCertificated && (
          <StyledScaleWrapper scale={scale} className="absolute w-full pointer-events-none">
            <div className="absolute left-0 flex w-full top-[1307px] justify-center">
              <img src={`${BASE_URL}/strike-out-black.webp`} alt="certified" className="w-[308px] h-auto" />
            </div>
          </StyledScaleWrapper>
        )}
        {ridiSsoLinked && (
          <StyledScaleWrapper scale={scale} className="absolute w-full pointer-events-none">
            <div className="absolute left-0 flex w-full top-[1376px] justify-center">
              <img src={`${BASE_URL}/strike-out-white.webp`} alt="linked" className="w-[308px] h-auto object-contain" />
            </div>
          </StyledScaleWrapper>
        )}
        <map name="image-map3">
          <area
            target="_self"
            alt="디리토 본인 인증"
            title="디리토 본인 인증"
            shape="rect"
            coords="125,3924,1046,4109"
            onClick={onClickUserCertification}
          />
          <area
            target="_self"
            shape="rect"
            coords="126,4127,1046,4322"
            onClick={onClickRidiSSoLink}
            alt="리디 계정 연결하기"
            title="리디 계정 연결하기"
          />
        </map>
      </div>
      {isLoggedIn && (
        <div className="relative w-full flex justify-center">
          <img id="image4" alt="vote_4" src={`${BASE_URL}/4.webp`} className="w-full h-auto block" />
          <StyledScaleWrapper scale={scale} className="absolute left-0 w-full pointer-events-none flex justify-center">
            <div className="absolute flex justify-between items-center top-[198px] w-[306px]">
              {GENRE_ORDER.map(genreId => (
                <div
                  key={genreId}
                  className="flex flex-col items-center justify-center font-medium text-black z-10 w-full h-[95px]">
                  <span className="font-bold text-[18px] leading-[1.2]">{genreCounts[genreId]}</span>
                  <span className="text-[16px] mt-[4px]">작품</span>
                  {(() => {
                    const isVoteCompletedToday = (voteData?.count?.[today]?.votedContentIds?.length ?? 0) >= 5;
                    const renderCompletedText = genreCounts[genreId] >= 2 || isVoteCompletedToday;
                    return (
                      <span className="text-[11px] mt-[2px" style={{ opacity: renderCompletedText ? 1 : 0 }}>
                        투표 완료
                      </span>
                    );
                  })()}
                </div>
              ))}
            </div>
          </StyledScaleWrapper>
        </div>
      )}

      <div className="relative w-full flex justify-center">
        <img id="image5" useMap="#image-map5" alt="vote_5" src={`${BASE_URL}/5.webp`} className="w-full h-auto block" />
        <StyledScaleWrapper scale={scale} className="absolute left-0 flex justify-center w-full pointer-events-none">
          <div className="absolute w-[310px] flex flex-wrap top-[147px]">
            {voteDates.map((date, index) => (
              <VoteStampSheet
                key={date}
                index={index}
                date={date}
                today={today}
                votedCount={voteData?.count?.[date]?.votedContentIds?.length ?? 0}
              />
            ))}
          </div>
        </StyledScaleWrapper>
        <map name="image-map5">
          <area
            target="_self"
            shape="rect"
            coords="123,1443,1047,1638"
            href="?tab=content"
            onClick={e => {
              e.preventDefault();
              window.history.replaceState(null, '', `?tab=content&target=rollings`);
            }}
            alt="픽앤츄 투표하러가기"
            title="픽앤츄 투표하러가기"
          />
        </map>
      </div>
      {isLoggedIn && (
        <div className="relative w-full flex justify-center">
          <img id="image6" alt="vote_6" src={`${BASE_URL}/6.webp`} className="w-full h-auto block" />
          <StyledScaleWrapper scale={scale} className="absolute w-full flex justify-center pointer-events-none">
            <div className="absolute flex justify-around w-[306px] top-[190px]">
              <div className="w-full flex justify-center font-bold text-black text-[18px]">{participationDays}</div>
              <div className="w-full flex justify-center font-bold text-black text-[18px]">
                {numberComma(earnedPoints)}
              </div>
            </div>
          </StyledScaleWrapper>
        </div>
      )}

      <div className="relative w-full flex justify-center">
        <img alt="vote_7" src={`${BASE_URL}/7.webp`} className="w-full h-auto block" />
      </div>

      <div className="relative w-full flex justify-center">
        <img
          id="image8"
          useMap="#image-map8"
          alt="vote_8"
          src={`${BASE_URL}/8-1.webp`}
          className="w-full h-auto block"
        />
        <map name="image-map8">
          <area
            target="_blank"
            shape="rect"
            coords="127,2666,1045,2854"
            href={HELP_QNA_URL}
            title="프로그램 안내 및 상세 FAQ"
            alt="프로그램 안내 및 상세 FAQ"
          />
        </map>
      </div>
    </div>
  );
};

export const StyledScaleWrapper = styled.div<{ scale: number }>`
  transform: scale(${({ scale = 0.3 }) => scale});
`;
